import { html, LitElement, nothing } from 'lit';
import { fs24Offer, fs24Edit } from '@cof/fs-icons';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { DebugService } from '../../shared/services/debug.service.js';
import { style } from './review-offer-card-details.style.js';
import { currencyFormatter } from '../../shared/formatters/currency.formatter.js';
import '@cof/fs-label-badge-wc/fs-label-badge.js';

FsIcon.add([fs24Edit, fs24Offer]);

class RefiReviewOfferCardDetails extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiReviewOfferCardDetails');
    this.hideSavingsPill = false;
  }

  static properties = {
    monthlyPayment: { type: String, attribute: 'monthly-payment' },
    offerApr: { type: String, attribute: 'offer-apr' },
    currentApr: { type: String, attribute: 'current-apr' },
    offerTerm: { type: String, attribute: 'offer-term' },
    currentTerm: { type: String, attribute: 'current-term' },
    lifetimeSavings: { type: String, attribute: 'lifetime-savings' },
    monthlySavings: { type: String, attribute: 'monthly-savings' },
    hideSavingsPill: { type: Boolean, attribute: 'hide-savings-pill' },
  };

  static styles = [style, fsTypography];

  render() {
    return html`
      <div class="mobile-view">
        <!-- inside box with contents-->
        <div class="inner-div">
          <!-- second box-->
          <div class="offer-details">
            <div class="offer-details-left">
              <fs-heading class="selected-payment fs-text fs-text-weight--semibold" size="xxlarge">
                <span class="fs-text fs-text-size--large-1">$</span>
                ${Math.round(this.monthlyPayment).toString().length > 3 ? html`<p class="fs-text fs-text-size--large-2">${Math.round(this.monthlyPayment)}</p>` : html`<p class="fs-text fs-text-size--large-3">${Math.round(this.monthlyPayment)}</p>`}
                <span class="fs-text fs-text-size--small-2">*</span>
              </fs-heading>
              <p class="fs-text fs-text-size--medium-2 fs-text-weight--semibold">/month</p>
            </div>

            <div class="mobile-vertical-divider"></div>

            <div class="offer-details-right">
              <!-- term, apr, pill, left border-->
              <p class="fs-text fs-text-weight--semibold fs-text-size--medium-1" id="selected-term">${this.offerTerm} months</p>
              <p class="fs-text fs-text-weight--semibold fs-text-size--medium-1" id="selected-apr">${this.offerApr}% APR</p>
              ${this.hideSavingsPill === true ? nothing : html`<fs-label-badge emphasis="medium" variant="positive" class="savings-pill"> ${this.savingsPillText()} </fs-label-badge>`}
            </div>
          </div>
        </div>
      </div>

      <!-- below html code is for desktop view only, hide for widths less than 744px -->
      <div class="desktop-view">
        <div class="middle-div">
          <div class="payment-div">
            <fs-heading class="selected-payment fs-text fs-text-weight--semibold" size="xxlarge">
              <p class="fs-text fs-text-size--large-1">$</p>
              ${Math.round(this.monthlyPayment).toString().length > 3 ? html`<p class="fs-text fs-text-size--large-2">${Math.round(this.monthlyPayment)}</p>` : html`<p class="fs-text fs-text-size--large-3">${Math.round(this.monthlyPayment)}</p>`}
              <p class="fs-text fs-text-size--medium-2">*</p>
            </fs-heading>
            <p class="fs-text fs-text-size--medium-3 fs-text-weight--semibold" style="margin: revert;">/month</p>
          </div>
          <div class="vertical-divider"></div>
          <div><p class="fs-text fs-text-size--medium-3 fs-text-weight--semibold" style="margin: revert;">${this.offerTerm} months</p></div>
          <div class="vertical-divider"></div>
          <div><p class="fs-text fs-text-size--medium-3 fs-text-weight--semibold" style="margin: revert;">${this.offerApr}% APR</p></div>
        </div>

        ${this.hideSavingsPill === true ? nothing : html`<div class="pill-div"><fs-label-badge emphasis="medium" variant="positive">${this.savingsPillText()}</fs-label-badge></div>`}
      </div>

      <!-- below html code is for small-mobile view only, hide for widths 375+ -->
      <div class="mobile-view-small">
        <!-- first box -->
        <div class="payment-div-small">
          <fs-heading class="selected-payment fs-text fs-text-weight--semibold" size="xxlarge">
            <p class="fs-text fs-text-size--large-1">$</p>
            ${Math.round(this.monthlyPayment).toString().length > 3 ? html`<p class="fs-text fs-text-size--large-2">${Math.round(this.monthlyPayment)}</p>` : html`<p class="fs-text fs-text-size--large-3">${Math.round(this.monthlyPayment)}</p>`}
            <p class="fs-text fs-text-size--medium-1">*</p>
          </fs-heading>
          <p class="fs-text fs-text-size--medium-2 fs-text-weight--semibold" style="margin: revert;">/month</p>
        </div>
        <!-- second box -->
        <div class="term-apr-div">
          <div><p class="fs-text fs-text-size--medium-1 fs-text-weight--semibold">${this.offerTerm} months</p></div>
          <div class="vertical-divider-small"></div>
          <div><p class="fs-text fs-text-size--medium-1 fs-text-weight--semibold">${this.offerApr}% APR</p></div>
        </div>
        <!-- third box -->
        ${this.hideSavingsPill === true ? nothing : html`<div class="pill-div-small"><fs-label-badge emphasis="medium" variant="positive">${this.savingsPillText()}</fs-label-badge></div>`}
      </div>
    `;
  }

  savingsPillText() {
    let savingsText = '';
    this.pillVariant = 'positive';
    if (this.monthlySavings && this.monthlySavings > 0) {
      savingsText = `Save ${currencyFormatter(Math.round(this.monthlySavings))} per month*`;
    } else if (this.offerTerm < this.currentTerm) {
      savingsText = 'Pay off faster';
    } else if (this.offerTerm === this.currentTerm) {
      savingsText = 'Keep your same term';
    } else if (this.offerApr < this.currentApr) {
      savingsText = 'Lower your APR';
    } else {
      savingsText = 'Longer term';
      this.pillVariant = 'warning';
    }
    return savingsText;
  }
}

customElements.define('refi-review-offer-card-details', RefiReviewOfferCardDetails);
